import React, { useCallback, useEffect, useMemo, useReducer } from "react";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import searchInsights from "search-insights";

import * as Sentry from "@sentry/browser";

import { Storefront } from "./storefront";
import { storefrontCartsPath } from "../../routes";
import { StorefrontConfig, StorefrontContext } from "../types/storefront";
import {
  useFetchCart,
  useFetchCatalog,
  useFetchDeals,
  useFetchOutOfStockList,
  useMutateCart
} from "./queries";
import { ReactComponent as AlertCircle } from "images/icons/alert-circle.svg?react";
import { BottomNav } from "./bottom_nav";
import { useShowAlert } from "src/components/alert";
import {
  buildUrlForState,
  deserializeShopState,
  FocusProduct,
  NavigateCatalog,
  NavigatePage,
  NavigationContext,
  navigationReducerWithConfig,
  NavigationState,
  StorefrontPage,
  UpdateSearch
} from "../types/navigation";
import { AlertProvider } from "src/components/alert";
import { CartItemEvent } from "src/analytics/cart_item_event_serializer";
import buildRemoveItemFromCart from "src/storefront/helpers/cart/remove_item_from_cart";
import buildAddToCart from "src/storefront/helpers/cart/add_to_cart";
import buildUpdateCart from "src/storefront/helpers/cart/update_cart";

const globalQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: Number.POSITIVE_INFINITY,
    }
  }
});

const StorefrontLoader = ({
  config,
  catalogId,
  ageVerifiedStatus,
  queryClient
}: {
  config: StorefrontConfig;
  catalogId: string;
  ageVerifiedStatus: boolean;
  queryClient?: QueryClient;
}): React.JSX.Element => {
  return (
    <StorefrontContext.Provider value={config}>
      <QueryClientProvider client={queryClient ?? globalQueryClient}>
        <AlertProvider>
          <StorefrontLoaderInner
            config={config}
            catalogId={catalogId}
            ageVerifiedStatus={ageVerifiedStatus}
          />
        </AlertProvider>
      </QueryClientProvider>
    </StorefrontContext.Provider>
  );
}

const searchAndQueryParamsFromUrl: () => [string, { [param: string]: string} ] = () => {
  const url = new URL(window.location.toString());
  const searchParams = url.searchParams;
  const searchQuery = url.searchParams.get("search_bar") || ""

  if (searchQuery !== "") {
    searchParams.delete("search_bar");
  }

  return [searchQuery, Object.fromEntries(searchParams)];
}

const currentPageFromUrl = (): StorefrontPage => {
  const splits = window.location.pathname.split("/");
  const lastSegment = splits[splits.length - 1];
  switch (lastSegment) {
    case "search":
      return StorefrontPage.Search;

    case "cart":
      return StorefrontPage.Cart;

    case "categories":
      return StorefrontPage.Categories;

    default:
      return StorefrontPage.Shop;
  }
}

const StorefrontLoaderInner = ({
  config,
  catalogId,
  ageVerifiedStatus,
}: {
  config: StorefrontConfig;
  catalogId: string;
  ageVerifiedStatus: boolean;
}): React.JSX.Element => {
  const calculateInitialNavigationState = useCallback(() => {
    const currentPage = currentPageFromUrl();
    const [searchQuery, queryParams] = searchAndQueryParamsFromUrl();
    const [focusedCatalogComponent, focusedProduct] = deserializeShopState(window.location.hash.replace("#", ""));

    if (focusedProduct.length > 1) {
      // Focus the product as separate history entry so that the browser back will always close it
      const initialState = new NavigationState(currentPage, searchQuery, focusedCatalogComponent, [], queryParams);
      window.history.replaceState(null, "", buildUrlForState(config, initialState));

      const initialStateWithProduct = new NavigationState(currentPage, searchQuery, focusedCatalogComponent, focusedProduct, queryParams);
      window.history.pushState(null, "", buildUrlForState(config, initialStateWithProduct));

      return initialStateWithProduct;
    } else {
      return new NavigationState(currentPage, searchQuery, focusedCatalogComponent, focusedProduct, queryParams);
    }
  }, [config]);

  const [navigationState, updateNavigation] = useReducer(navigationReducerWithConfig(config), null, calculateInitialNavigationState);

  const catalogQuery = useFetchCatalog();
  const outOfStockQuery = useFetchOutOfStockList();
  const showAlert = useShowAlert();
  const cartQuery = useFetchCart();
  const activeDealsQuery = useFetchDeals();

  const onUrlChange = useCallback((e: Event) => {
    e.preventDefault();

    const newPage = currentPageFromUrl();
    if (navigationState.currentPage != newPage) {
      updateNavigation(NavigatePage.action(newPage, false));
    }

   const [newFocusedCatalogComponent, newFocusedProduct] = deserializeShopState(window.location.hash.replace("#", ""));
   updateNavigation(NavigateCatalog.action(newFocusedCatalogComponent, false));
   updateNavigation(FocusProduct.action(newFocusedProduct, false));
    
    const [newSearchQuery, _] = searchAndQueryParamsFromUrl();
    if (newPage === StorefrontPage.Search && navigationState.searchQuery !== newSearchQuery)
      updateNavigation(UpdateSearch.action(newSearchQuery, false));
  }, [navigationState.currentPage, navigationState.searchQuery, updateNavigation]);

  useEffect(() => {
    searchInsights("init", {
      appId: config.searchAppId,
      apiKey: config.searchPublicApiKey,
      userToken: config.searchCustomerToken,
      authenticatedUserToken: config.searchAuthenticatedUserToken,
    });
  }, [config.searchAppId, config.searchAuthenticatedUserToken, config.searchCustomerToken, config.searchPublicApiKey]);

  useEffect(() => {
    window.addEventListener("popstate", onUrlChange);
    return () => {
      window.removeEventListener("popstate", onUrlChange);
    }
  }, [onUrlChange]);

  const cartMutation = useMutateCart({
    updateCartUrl: storefrontCartsPath(config.storefrontId, { catalog_id: catalogId }),
    csrfToken: config.csrfToken,
  });

  const pushDataEvent = (cartItemEvent: CartItemEvent) => {
    window.dataLayer.push(cartItemEvent);
  }

  const addToCart = useMemo(() => buildAddToCart(cartQuery.data, catalogQuery.data, config, navigationState, cartMutation.mutateAsync, showAlert, pushDataEvent), [cartMutation.mutateAsync, cartQuery.data, catalogQuery.data, config, navigationState, showAlert]);
  const removeItemFromCart = useMemo(() => buildRemoveItemFromCart(cartQuery.data, catalogQuery.data, config, cartMutation.mutateAsync, showAlert, pushDataEvent), [cartMutation, cartQuery.data, catalogQuery.data, config, showAlert]);
  const updateCart = useMemo(() => buildUpdateCart(cartQuery.data, catalogQuery.data, config, cartMutation.mutateAsync, showAlert, pushDataEvent), [cartMutation, cartQuery.data, catalogQuery.data, config, showAlert]);

  const loadingError = <>
    <div className="container space-mt-extra-desktop">
      <div className="callout has-coloured-icon has-border">
        <AlertCircle className="icon streamline-icon" />
        <div>
          <div className="title">We couldn&apos;t load this store</div>
          <p>Sorry \u2014 we&apos;re having an issue loading this store. Check your internet and try again, or otherwise contact our customer service team for help.</p>
        </div>
      </div>
    </div>
  </>;

  /*
    Everything in here replaces identical content in catalog_show.html.erb so that we
    avoid a flash of unloaded content. This is basically a super lightweight and somewhat
    fragile version of server side rendering.
  */ 
  const loadingSkeleton = <>
    <div className="divider has-content has-high-contrast-above-tablet choice-strip">
      <div className="skeleton is-chip"></div>
      <div className="skeleton is-chip space-ml-xs"></div>
    </div>

    <div className="grid has-aside" role="alert" aria-label="Loading store catalog">
      <div className="column has-container-spacing-top-above-tablet">
        <section className="has-lowered-contrast-above-tablet">
          <div className="container has-lowered-contrast">
            <div className="catalog-section-heading">
              <div className="skeleton is-heading is-small"></div>
              <div className="skeleton is-paragraph" style={{ "--lines": 1 } as React.CSSProperties}></div>
            </div>
          </div>

          <div className="container">
            <a className="product-listing">
              <div className="skeleton is-paragraph space-mt-xs space-mb-lg"></div>
            </a>

            <a className="product-listing">
              <div className="skeleton is-paragraph space-mt-xs space-mb-lg"></div>
            </a>

            <a className="product-listing">
              <div className="skeleton is-paragraph space-mt-xs space-mb-lg"></div>
            </a>
          </div>
        </section>
      </div>
    </div>
  </>;

  const inner = (() => {
    if (catalogQuery.isError || cartQuery.isError || outOfStockQuery.isError || activeDealsQuery.isError) {
      const errorMessage = catalogQuery.error || cartQuery.error || outOfStockQuery.error || activeDealsQuery.error;
      Sentry.captureMessage("Error loading catalog", { extra: { error_message: errorMessage, catalog_url: config.catalogUrl } });

      return loadingError;
    } else if (catalogQuery.isPending || cartQuery.isPending || outOfStockQuery.isLoading || activeDealsQuery.isPending) {
      return (loadingSkeleton);
    } else {

      return (
        <Storefront
          ageVerifiedStatus={ageVerifiedStatus}
          addToCart={addToCart}
          updateCart={updateCart}
          removeItemFromCart={removeItemFromCart}
          cartLoading={cartMutation.isPending}
        />
      );
    }
  })();

  return (
      <StorefrontContext.Provider value={config}>
        <NavigationContext.Provider value={[navigationState, updateNavigation]}>
          {inner}

          {config.usesNewBottomNav ? <BottomNav/> : <></>}
        </NavigationContext.Provider>
      </StorefrontContext.Provider>
  );
}

export { StorefrontLoader };
